import { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Send, Loader2, Trash2, Plus, MessageSquare, X, Bot, User, Zap } from "lucide-react";

// Iter 283 — widget re-pointé vers le nouveau système Agent IA (orchestrateur + 4 thématiques)
const LEGACY_KEY_MAP = {
  comptabilite: "comptabilite",
  crm: "vente",
  vente: "vente",
  marketing: "marketing",
  mail: "marketing",
  qualiopi: "formation",
  formation: "formation",
  conseiller: "orchestrateur",
  gestion: "comptabilite",
  intel: "vente",
  strategy: "orchestrateur",
  orchestrateur: "orchestrateur",
};

const MARKETING_SUGGESTIONS = [
  "Crée 3 posts LinkedIn pour cette semaine sur nos services",
  "Planifie un calendrier éditorial pour le mois prochain (2 posts/semaine)",
  "Rédige une newsletter mensuelle pour nos clients",
  "Crée une séquence de bienvenue en 3 emails pour les nouveaux clients",
];

export function AgentChat({ agentType, agentName, agentColor, context, onClose }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const agentKey = LEGACY_KEY_MAP[agentType] || "orchestrateur";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Changement d'agent → nouvelle conversation (évite le mélange de sessions)
    setSessionId("");
    setMessages([]);
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentKey]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const r = await api.get(`/ai/sessions?agent_key=${agentKey}`);
      setSessions(r.data || []);
    } catch {}
  };

  const loadSession = async (sid) => {
    try {
      const r = await api.get(`/ai/sessions/${sid}`);
      setMessages(r.data.messages || []);
      setSessionId(sid);
      setShowSessions(false);
    } catch {}
  };

  const deleteSession = async (sid) => {
    await api.delete(`/ai/sessions/${sid}`);
    if (sessionId === sid) { setSessionId(""); setMessages([]); }
    loadSessions();
  };

  const newConversation = () => {
    setSessionId("");
    setMessages([]);
    setShowSessions(false);
    inputRef.current?.focus();
  };

  const sendMessage = async (overrideMsg) => {
    const msg = (overrideMsg || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg, timestamp: new Date().toISOString() }]);
    setLoading(true);
    try {
      const r = await api.post("/ai/chat", { agent_key: agentKey, message: msg, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages(prev => [...prev, {
        role: "assistant", content: r.data.response, timestamp: new Date().toISOString(),
        agent: r.data.routed_to, agent_name: r.data.agent_name, agent_color: r.data.agent_color, routed: r.data.routed,
      }]);
      loadSessions();
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: `Erreur: ${e.response?.data?.detail || "Impossible de contacter l'agent"}`, timestamp: new Date().toISOString() }]);
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col h-full bg-[#161020] rounded-xl border border-white/10 overflow-hidden" data-testid={`agent-chat-${agentType}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10" style={{ background: `${agentColor}08` }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${agentColor}20` }}>
          <Bot className="w-4 h-4" style={{ color: agentColor }} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold" style={{ color: agentColor }}>{agentName}</div>
          <div className="text-[10px] text-slate-500">Agent IA</div>
        </div>
        <button onClick={() => setShowSessions(!showSessions)} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400" data-testid="agent-sessions-btn">
          <MessageSquare className="w-4 h-4" />
        </button>
        <button onClick={newConversation} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400" data-testid="agent-new-chat-btn">
          <Plus className="w-4 h-4" />
        </button>
        {onClose && (
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400"><X className="w-4 h-4" /></button>
        )}
      </div>

      {/* Sessions panel */}
      {showSessions && (
        <div className="border-b border-white/10 max-h-48 overflow-y-auto bg-[#0b0811]">
          {sessions.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-500">Aucune conversation</div>
          ) : sessions.map(s => (
            <div key={s.session_id} className={`flex items-center gap-2 px-4 py-2.5 hover:bg-white/5 cursor-pointer border-b border-white/5 ${sessionId === s.session_id ? "bg-white/5" : ""}`}>
              <button onClick={() => loadSession(s.session_id)} className="flex-1 text-left min-w-0">
                <div className="text-xs text-slate-300 truncate">{s.preview || "Conversation"}</div>
                <div className="text-[10px] text-slate-600">{new Date(s.updated_at || s.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
              </button>
              <button onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }} className="p-1 hover:bg-red-500/10 rounded">
                <Trash2 className="w-3 h-3 text-red-400" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0" style={{ maxHeight: "400px" }}>
        {messages.length === 0 && (
          <div className="text-center py-6">
            <Bot className="w-10 h-10 mx-auto mb-3" style={{ color: `${agentColor}40` }} />
            <p className="text-sm text-slate-500 mb-4">Comment puis-je vous aider ?</p>
            {agentKey === "marketing" && (
              <div className="grid grid-cols-1 gap-1.5 max-w-md mx-auto" data-testid="marketing-suggestions">
                {MARKETING_SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => sendMessage(s)}
                    className="text-left text-[11px] text-slate-400 px-3 py-2 rounded-lg border border-white/5 hover:border-[#ec4899]/30 hover:text-[#ec4899] transition-colors bg-[#0b0811]"
                    data-testid={`suggestion-${i}`}>
                    <Zap className="w-3 h-3 inline mr-1.5 text-[#ec4899]/40" />{s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : ""}`}>
            {msg.role === "assistant" && (
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${msg.agent_color || agentColor}20` }}>
                <Bot className="w-3.5 h-3.5" style={{ color: msg.agent_color || agentColor }} />
              </div>
            )}
            <div className={`max-w-[80%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
              msg.role === "user"
                ? "bg-[#f7c935]/10 text-white border border-[#f7c935]/20"
                : "bg-[#14101c] text-slate-200 border border-white/5"
            }`}>
              {msg.role === "assistant" && msg.routed && msg.agent_name && (
                <div className="text-[10px] font-bold mb-1" style={{ color: msg.agent_color || agentColor }} data-testid="routed-agent-badge">
                  {msg.agent_name}
                </div>
              )}
              <div className="whitespace-pre-wrap">{msg.content}</div>
            </div>
            {msg.role === "user" && (
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#f7c935]/20">
                <User className="w-3.5 h-3.5 text-[#f7c935]" />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex gap-2.5">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${agentColor}20` }}>
              <Bot className="w-3.5 h-3.5 animate-pulse" style={{ color: agentColor }} />
            </div>
            <div className="bg-[#14101c] border border-white/5 rounded-xl px-4 py-3">
              <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-white/10 bg-[#0b0811]">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
            placeholder="Écrivez votre message..."
            className="flex-1 bg-[#14101c] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#f7c935]/50 focus:outline-none"
            disabled={loading}
            data-testid="agent-chat-input"
          />
          <Button onClick={() => sendMessage()} disabled={loading || !input.trim()}
            className="px-3 font-bold" style={{ background: agentColor, color: "#14101c" }}
            data-testid="agent-chat-send">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
